import React from 'react';
import clsx from 'clsx';
import {
  Card,
  Heading,
  PageLevelNotification,
} from '@americanexpress/dls-react';
import { useRouteParams } from '@/context/routeParamsContext';
import {
  useLoungeImportantInformation,
  useLoungeProviderId,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import getStableHash from '@/helpers/getStableHash';
import styles from './LoungeDetails.module.scss';

const getImportantInformationItemQeId = (category, item) => {
  return `important-information-item-${category}-${getStableHash(item)}`;
};

// eslint-disable-next-line complexity -- pre-existing complexity, refactor separately
const LoungeImportantInformation = () => {
  const { lounges } = useCommonContent();
  const { loungeId } = useRouteParams();
  const providerId = useLoungeProviderId(loungeId);
  const importantInfo = useLoungeImportantInformation(loungeId);
  const isImportantInfoPresent =
    importantInfo?.general?.length > 0 ||
    importantInfo?.warning?.length > 0 ||
    importantInfo?.regulatory?.length > 0 ||
    importantInfo?.notification?.length > 0;

  const notifications = importantInfo?.notification?.reduce(
    (acc, item, index) => {
      if (index % 2 === 0) {
        // Use the current item as the heading and the next item as the info
        acc.push({
          heading: item,
          info: importantInfo?.notification?.[index + 1],
        });
      }
      return acc;
    },
    []
  );

  const disclaimer = lounges?.accessDisclaimer?.find((providerDisclaimer) => {
    return providerDisclaimer?.provider_id?.includes(providerId);
  })?.disclaimer;

  const disclaimerCard = (
    <Card className={clsx(styles.disclaimer, 'tw-mt-6')}>
      <div
        className="tw-text-xs tw-text-gray-700 a-children_tw-underline"
        dangerouslySetInnerHTML={{ __html: disclaimer || '' }}
      />
    </Card>
  );

  if (!isImportantInfoPresent && !disclaimer) {
    return null;
  }

  if (!isImportantInfoPresent && disclaimer) {
    return (
      <div
        className="tw-mb-6 tw-border-b tw-border-gray-300 tw-pb-6"
        data-qe-id="important-information-section"
      >
        <Heading
          level={2}
          variant="sans-medium-regular"
          className="tw-mb-6 tw-text-gray-900"
          data-qe-id="important-information-heading"
        >
          {lounges?.importantInfo?.header}
        </Heading>
        {disclaimerCard}
      </div>
    );
  }

  return (
    <div
      className="tw-mb-6 tw-border-b tw-border-gray-300 tw-pb-6"
      data-qe-id="important-information-section"
    >
      <Heading
        level={2}
        variant="sans-medium-regular"
        className="tw-mb-6 tw-text-gray-900"
        data-qe-id="important-information-heading"
      >
        {lounges?.importantInfo?.header}
      </Heading>
      {notifications?.length > 0 && (
        <ul id="important-notifications" className="tw-mb-4 tw-space-y-4">
          {notifications.map((notification, i) => (
            // eslint-disable-next-line react/no-array-index-key -- no other item to add as key
            <li key={`notification-${i}`}>
              <PageLevelNotification
                type="caution"
                isDismissible={false}
                headingSlot={
                  <h2 className="tw-pr-4">{notification.heading}</h2>
                }
              >
                <p className="tw-pr-4 tw-pt-2">{notification.info}</p>
              </PageLevelNotification>
            </li>
          ))}
        </ul>
      )}
      <ul
        id="important-info"
        className="tw-list-disc tw-space-y-4 tw-pl-6 tw-text-gray-800"
        data-qe-id="important-information-list"
      >
        {importantInfo.regulatory?.map((item) => (
          <li
            key={`regulatory-${item}`}
            data-qe-id={getImportantInformationItemQeId('regulatory', item)}
          >
            {item}
          </li>
        ))}
        {importantInfo.warning?.map((item) => (
          <li
            key={`warning-${item}`}
            data-qe-id={getImportantInformationItemQeId('warning', item)}
          >
            {item}
          </li>
        ))}
        {importantInfo.general?.map((item) => (
          <li
            key={`general-${item}`}
            data-qe-id={getImportantInformationItemQeId('general', item)}
          >
            {item}
          </li>
        ))}
      </ul>
      {disclaimer && disclaimerCard}
    </div>
  );
};

export default LoungeImportantInformation;
